import { useState } from 'react';
import Modal from '../core/Modal';
import ConfirmationModal from '../core/ConfirmationModal';
import { useTranslation } from '../../i18n/useTranslation';

// Use raw client for auth operations
import { supabaseRaw as supabase } from '../../lib/supabase';

const ForgotPasswordModal = ({ visible = false, onClose = null }) => {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Sync submit wrapper — preventDefault must run before any await (see the form's note below).
  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit();
  };

  const handleSubmit = async () => {
    setError(null);

    if (!email.trim()) {
      setError(t('email_required_error'));
      return;
    }

    if (!validateEmail(email)) {
      setError(t('email_invalid_error'));
      return;
    }

    setLoading(true);

    try {
      // Use a non-hash path so Supabase can append #access_token=...&type=recovery
      // (hash-based routes cause double-# which breaks Supabase token detection)
      const redirectUrl = window.location.origin + '/auth/reset-password';

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        email.trim().toLowerCase(),
        { redirectTo: redirectUrl }
      );

      if (resetError) {
        console.error('Password reset error:', resetError);
        // Don't reveal if email exists or not for security
      }

      // Always show success message to prevent email enumeration
      setShowConfirmation(true);
    } catch (err) {
      console.error('Password reset error:', err);
      // Still show success message for security
      setShowConfirmation(true);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setEmail('');
    setError(null);
    if (onClose) {
      onClose();
    }
  };

  const handleConfirmationOk = () => {
    setShowConfirmation(false);
    setEmail('');
    setError(null);
    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      <Modal
        isOpen={visible}
        onClose={handleBack}
        panelClassName="rounded-3xl shadow-2xl w-full max-w-md mx-4 p-8"
        panelStyle={{ background: '#024B99' }}
      >

        {/* OurNet Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="/images/logos/logo-ournet.png"
            alt="OurNet"
            style={{ width: '80px', height: '80px', objectFit: 'contain' }}
          />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-center mb-3" style={{ color: 'white' }}>
          {t('forgot_password_title')}
        </h2>

        {/* Subtitle */}
        <div className="text-center mb-6" style={{ color: 'white', fontSize: '14px', lineHeight: '1.5' }}>
          <p>{t('forgot_password_instructions')}</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* Email Field */}
        {/* 2026-09-25 sign-in-forms build: the fields and the primary action sit inside a real
            <form> so Chrome and Android autofill see a submission and offer to save or fill.
            onSubmit calls a SYNCHRONOUS wrapper whose first statement is preventDefault() —
            passing the async handler directly would let the browser submit natively as GET and
            write the credentials into the URL. noValidate keeps the app's own translated
            validation authoritative; type="email" would otherwise raise the browser's own
            bubble and the handler would never run. */}
        <form onSubmit={handleFormSubmit} noValidate>
          <div className="mb-6">
            <label htmlFor="forgot-email" className="block text-sm font-medium mb-2" style={{ color: 'white' }}>
              {t('email_label')}
            </label>
            <input
              type="email"
              name="email"
              autoComplete="username"
              id="forgot-email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
              placeholder={t('email_placeholder')}
              className="w-full px-4 py-3 border-0 rounded-lg focus:ring-2 focus:ring-white focus:outline-none"
              style={{
                fontSize: '14px',
                background: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                backdropFilter: 'blur(10px)'
              }}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-4 justify-center">
            <button
              type="button"
              onClick={handleBack}
              className="px-8 py-3 rounded-lg font-semibold transition-colors"
              style={{
                background: 'white',
                border: '2px solid white',
                color: '#024B99',
                minWidth: '150px'
              }}
            >
              {t('common:cancel').toUpperCase()}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-3 rounded-lg font-semibold text-white transition-colors disabled:opacity-50"
              style={{
                background: '#5DBEA3',
                border: 'none',
                minWidth: '150px'
              }}
            >
              {loading ? t('sending_button').toUpperCase() : t('submit_button').toUpperCase()}
            </button>
          </div>
        </form>

      </Modal>

      <ConfirmationModal
        visible={showConfirmation}
        title={t('success_title')}
        message={t('success_message')}
        onOk={handleConfirmationOk}
      />
    </>
  );
};

export default ForgotPasswordModal;
